import React, { useState } from 'react';
import { Button, Modal, Space, Spin, Table, Tag, Typography } from 'antd';
import { getDuration, getStatus } from '../../../../../../Utils/data';
import { logs as LOGS } from '../../../../../../API/TT_ELD/logs';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import BulkEditModal from './BulkEditModal';
import { LockFilled } from '@ant-design/icons';
import moment from 'moment';

import DebugInfoModal from './Log/DebugInfoModal';
const { Paragraph } = Typography;

type numStr = number | string;
type strUnd = string | undefined | null;

interface logDataSource {
  no: numStr;
  id: numStr;
  status: string | null;
  start_date: strUnd;
  endDate: strUnd;
  duration: undefined | string;
  address: { address: string; lat: number | string; lng: number | string };
  odometer: numStr | undefined;
  engineHours: numStr | undefined;
  note: strUnd;
  document: numStr;
  createdAt: strUnd;
  updatedAt: strUnd;
  trailer: numStr;
  debug_info: string | undefined | null;
  actions: {
    id: numStr;
    queryClient: any;
    userId: string | undefined;
    companyId: number;
    record_status: number | undefined | null;
    inspection: boolean;
  };
  vehicle: {
    truck_number: number | string;
    id: number | string;
  };
  key: React.Key;
}

function LogsTable({
  logs,
  userId,
  data2,
  recordStatus,
  isLoading,
  isFetching,
  refetch,
  setIsModalVisible,
  open,
  setOpen,
  toDayDate,
}: {
  logs: any;
  userId: string | undefined;
  data2: any;
  recordStatus: number;
  isLoading: boolean | undefined;
  isFetching: any;
  refetch: () => void;
  setIsModalVisible(isModalVisible: any): void;
  open: any;
  setOpen(open: any): void;
  toDayDate: string | any;
}) {
  const [debugModalVisible, setDebugModalVisible] = useState(false);
  const [selectedDebugInfo, setSelectedDebugInfo] = useState<string>('');
  const queryClient: any = useQueryClient();
  const [getKey, setGetKey] = useState<React.Key[]>([]);
  const rowSelection = {
    onChange: (selectedRowKeys: React.Key[], selectedRows: logDataSource[]) => {
      if (selectedRows.length > 0) {
        setIsModalVisible(true);
      } else {
        setIsModalVisible(false);
      }
      setGetKey(selectedRowKeys);
    },
  };

  const columns: object[] = [
    {
      title: 'No',
      dataIndex: 'no',
      key: 'no',
      width: 50,
    },
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 100,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      editable: true,
      width: 130,
      render: (tag: any) => {
        const TagColors: any = {
          login: 'purple',
          sleep: '#ffbb00',
          driving: '#27ae60',
          poweroff: '#858e9e',
          off: '#858e9e',
          on: '#1470c6',
          intermediate: '#ff7a45',
          personal: '#d48806',
          yard: '#1d39c4',
          logout: '#f2438d',
          poweron: '#52c41a',
          certify: '#c41d7f',
          adverse_driving: '#2d2fc4',
          short_haul: '#c41c1b',
          malfunction_1_7: '#d9d766',
          malfunction_2_7: '#d98711',
          malfunction_3_7: '#5dd9ac',
          malfunction_4_7: '#a69fd9',
          malfunction_5_7: '#d97263',
          malfunction_6_7: '#68d976',
          eld_connect: '#009b14',
          eld_disconnect: '#c41c1b',
          internet_disconnect: '#c41d7f',
          internet_connect: '#006418',
          app_worked: '#5dd9ac',
          app_paused: '#bec0c5',
          battery: '#F99E2C',
        };
        const tagColor = TagColors[tag] || 'defaultColor';

        return (
          <>
            <Tag
              style={{ background: tagColor, color: 'white' }}
              color="white"
              className="tag-hover-effect"
            >
              {tag?.toUpperCase()}
            </Tag>
          </>
        );
      },
    },
    {
      title: 'Start Date',
      dataIndex: 'start_date',
      key: 'start_date',
      editable: true,
      width: 200,
    },
    {
      title: 'End Date',
      dataIndex: 'endDate',
      key: 'endDate',
      editable: true,
      width: 200,
    },
    {
      title: 'Duration',
      dataIndex: 'duration',
      key: 'duration',
      width: 120,
    },
    {
      title: 'Location',
      dataIndex: 'address',
      key: 'address',
      width: 250,
      render: ({
        address,
        lat,
        lng,
      }: {
        address: string;
        lat: number | string | undefined | any;
        lng: number | string | undefined;
      }) => {
        return (
          <>
            {address ? (
              <Paragraph style={{ marginBottom: 0 }} copyable={{ text: `${lat}, ${lng}` }}>
                {address}
              </Paragraph>
            ) : (
              ''
            )}
          </>
        );
      },
    },
    {
      title: 'Truck Number',
      dataIndex: ['vehicle', 'truck_number'],
      key: 'truck_number',
      width: 130,
    },
    {
      title: 'Odometer',
      dataIndex: 'odometer',
      key: 'odometer',
      width: 100,
    },
    {
      title: 'Engine Hours',
      dataIndex: 'engineHours',
      key: 'engineHours',
      width: 130,
    },

    {
      title: 'Document',
      dataIndex: 'document',
      key: 'document',
      width: 170,
    },
    {
      title: 'Trailer',
      dataIndex: 'trailer',
      key: 'trailer',
      width: 200,
    },
    {
      title: 'Note',
      dataIndex: 'note',
      key: 'note',
      width: 200,
    },

    {
      title: 'Created at',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 200,
    },
    {
      title: 'Updated at',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 200,
    },
    {
      title: 'Debug Info',
      dataIndex: 'debug_info',
      key: 'debug_info',
      width: 130,
      render: (debugInfo: string) => (
        <Button
          type="link"
          onClick={() => {
            setSelectedDebugInfo(debugInfo);
            setDebugModalVisible(true);
          }}
        >
          View
        </Button>
      ),
    },
    {
      title: 'Actions',
      dataIndex: 'actions',
      key: 'actions',
      width: 180,
      fixed: 'right',
      render: ({
        id,
        queryClient,
        userId,
        companyId,
        record_status,
        inspection,
      }: {
        id: number | string;
        queryClient: any;
        userId: numStr;
        companyId: number;
        record_status: number | undefined | null;
        inspection: boolean;
      }) => {
        return (
          <Space>
            <Link
              to={record_status !== 0 ? `log/${id}/${companyId}` : `logzeros/${id}/${companyId}`}
            >
              <Button>Edit</Button>
            </Link>
            <Button
              type="primary"
              danger
              onClick={() => {
                Modal.confirm({
                  title: 'Confirm',
                  icon: <ExclamationCircleOutlined rev={undefined} />,
                  content: 'Do you want to delete this log?',
                  okText: 'Ok',
                  cancelText: 'cancel',
                  onOk: async () => {
                    await LOGS.updateLogs(
                      { id, record_status: 2 },
                      companyId,
                      record_status !== 0 ? 'log' : 'logzeros',
                    );
                    queryClient.refetchQueries(`logs/${userId}`, { active: true });
                  },
                });
              }}
            >
              Delete
            </Button>
            {inspection && (
              <LockFilled style={{ color: 'green', fontSize: '16px' }} rev={undefined} />
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <div>
      <Spin size="large" spinning={isLoading || isFetching}>
        <BulkEditModal
          getKey={getKey}
          recordStatus={recordStatus}
          data2={data2?.data}
          refetch={refetch}
          open={open}
          setOpen={setOpen}
          companyId={logs?.find((el: any) => el.companyId)?.companyId}
          todayDate={toDayDate}
        />
        <Table
          style={{ margin: '15px 0 0 0' }}
          size="small"
          dataSource={logs?.map((u: any, i: number): logDataSource => {
            let create = u.createdAt;
            let updated = u.updatedAt;

            const obj: logDataSource = {
              no: i + 1,
              id: u.id,
              status: getStatus(u?.event_code, u?.event_type),
              start_date: u.start_date
                ? `${moment.utc(u.start_date).format('YYYY-MM-DD, h:mm:ss A')} (UTC${moment
                    .utc(u.start_date)
                    .format('Z')})`
                : '',
              endDate: u.end_date
                ? `${moment.utc(u.end_date).format('YYYY-MM-DD, h:mm:ss A')} (UTC${moment
                    .utc(u.end_date)
                    .format('Z')})`
                : '',
              duration: getDuration(u.start_date, u.end_date),
              address: { address: u.address, lat: u?.coordinates?.lat, lng: u?.coordinates?.lng },
              odometer: u.odometr,
              engineHours: u.engine_hours,
              note: u.note,
              document: u.document,
              createdAt: create
                ? `${moment.utc(create).format('YYYY-MM-DD, h:mm:ss A')} (UTC${moment
                    .utc(create)
                    .format('Z')})`
                : '',
              updatedAt: updated
                ? `${moment.utc(updated).format('YYYY-MM-DD, h:mm:ss A')} (UTC${moment
                    .utc(updated)
                    .format('Z')})`
                : '',
              debug_info: u?.debug_info,
              trailer: u?.trailer,
              actions: {
                id: u.id,
                queryClient,
                userId,
                companyId: u?.companyId,
                record_status: u.record_status,
                inspection: u.inspection,
              },
              vehicle: {
                id: u.vehicle?.id,
                truck_number: u.vehicle?.truck_number,
              },
              key: u.id,
            };
            return obj;
          })}
          columns={columns}
          pagination={false}
          rowSelection={{
            type: 'checkbox',
            ...rowSelection,
          }}
          scroll={{ x: 'max-content', y: 660 }}
          sticky
        />

        <DebugInfoModal
          visible={debugModalVisible}
          onClose={() => setDebugModalVisible(false)}
          debugInfo={selectedDebugInfo}
        />
      </Spin>
    </div>
  );
}

export default LogsTable;
